import { useState, useEffect, useRef, useMemo } from "react";
import { useUserProgress } from "../hooks/useUserProgress";
import SocialBar from "./SocialBar";
import MarcaMiPAE from "./MarcaMiPAE";
import { Sparkles, BookOpen, Zap, Activity, Droplets, ChevronRight, ClipboardList, Pill, ShieldCheck, ShieldAlert, ClipboardCheck, GraduationCap, ListChecks, Library, FlaskConical } from "lucide-react";
import { trackLanding, trackLandingVista } from "../lib/landingTracking";
import { puedeAbrir } from "../lib/accessGate";
import { calcularTurnoDelDia, toDateKey, inicioDeSemana } from "../utils/calendario";
import { supabase } from "../lib/supabase";
import { enviarCodigoEmail, verificarCodigoEmail, entrarConClave } from "../lib/emailAuth";
import { cargarRegistros, cargarRonda, getCierreTurnoVisto, fechaHoyKey } from "../lib/registroTurnoStore";
import CockpitTurno from "./preparaTurno/CockpitTurno";
import PortadaPublica from "./presentacion/PortadaPublica";
import BenitoAvatar from "./BenitoAvatar";
import Banderines from "./home/Banderines";
import EnlaceDieciocho from "./home/EnlaceDieciocho";
import CierreTurno from "./preparaTurno/CierreTurno";
import QueCambioCard from "./preparaTurno/QueCambioCard";
import { purgarExpirados, resumenTurno } from "../lib/registroTurno";
import { faseDelTurno, faseDelTurnoActual, turnoAnclaPostNoche, TIPO_TURNO_LABEL } from "../lib/turnoCockpit";
import { turnoVigente, resumenTurnoVivo, diasFuera, saludoBenito, chipsBenito } from "../lib/benitoContexto";
import { cargarHilo, temaParaRetomar } from "../lib/companeroHilo";
import { leerResumenTurnoTens } from "../lib/tensResumen";
import { diasActivosLocal } from "../lib/rachaDiaria";
import { trackEvent } from "../lib/eventTracking";
import { MODULOS } from "../lib/modulos";
import { BienvenidaCurso, CasoCursoCard } from "./curso/CasoCurso";
import { cerrarCurso, cursoActivo } from "../lib/cursoPaliativos";
import { esElegibleTurno, esCapaEjercicio } from "../lib/perfil";
import { esTens } from "../lib/tens";
import { tarjetasExplora, tituloAtajos, actividadesRacha } from "../lib/homeExplora";
import { APPLE_LOGIN_DISPONIBLE, GOOGLE_LOGIN_DISPONIBLE } from "../lib/nativeAuth";
import HomeCard from "./foundational/HomeCard";
import SemanaTurnos from "./home/SemanaTurnos";
import NpsCard from "./home/NpsCard";
import RachaDiariaWidget from "./home/RachaDiariaWidget";
import MomentoEstudiante from "./home/MomentoEstudiante";
import FilaPaeSinTerminar from "./home/FilaPaeSinTerminar";
import MomentoTens from "./home/MomentoTens";
import Card from "./foundational/Card";
import Heading from "./foundational/Heading";
import Button from "./foundational/Button";
import NudgeCard from "./NudgeCard";
import { SHELL_ESCRITORIO_ENABLED } from "../lib/shellEscritorio";
import { nombreServicio } from "../constants/rotaciones";
import { AppleLogo } from "./icons/AppleLogo";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// El logo de Apple vive en ./icons/AppleLogo con su procedencia y su test: es
// arte de Apple y no se dibuja a mano (guideline 4, rechazo del 2026-09-18).
// Como el de Google, su color es de MARCA (currentColor sobre el botón
// negro/blanco que exige la guía de Apple), no un token del sistema.

// ─── Continuar con Apple ─────────────────────────────────────────────────────
// Solo en la app de iPhone (APPLE_LOGIN_DISPONIBLE). Existe por la guideline 4.8
// de la App Store: con Google en la pantalla, Apple exige su propia puerta al
// lado y no acepta el código por correo como equivalente (2026-09-16, primer
// rechazo de la 1.3.0). Va PRIMERO y del mismo tamaño que Google porque la guía
// de Apple exige que su botón no sea menos visible que el de terceros.
// Negro con texto blanco (blanco con texto negro en oscuro) es el diseño que
// Apple fija para este botón: colores de marca, no tokens, mismo criterio que
// GoogleIcon. Si la persona cierra la hoja sin entrar, no se muestra nada.
function BotonApple({ onLoginApple, onTrack }) {
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);
  if (!APPLE_LOGIN_DISPONIBLE || !onLoginApple) return null;

  const entrar = async () => {
    setError(null);
    setCargando(true);
    onTrack?.();
    try {
      const res = await onLoginApple();
      if (res?.error && !res.error.cancelado) setError(res.error.message || "No se pudo iniciar sesión con Apple. Reintenta.");
    } catch {
      setError("No se pudo iniciar sesión con Apple. Reintenta.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={cargando ? undefined : entrar}
        disabled={cargando}
        aria-busy={cargando || undefined}
        className="w-full inline-flex items-center justify-center gap-2 font-sans font-semibold rounded-tk-md border px-5 py-2.5 text-body min-h-[44px] bg-black text-white border-black dark:bg-white dark:text-black dark:border-white transition-all duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:cursor-wait"
      >
        <AppleLogo />
        <span>{cargando ? "Entrando…" : "Continuar con Apple"}</span>
      </button>
      {error && (
        <p className="mt-2 text-center text-small text-system-error font-sans" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Saludo dinámico ─────────────────────────────────────────────────────────

export function getSaludoPorHora(hora, primerNombre) {
  const nombre = primerNombre ? `, ${primerNombre}.` : '.';
  if (hora >= 6 && hora <= 11) return `Buenos días${nombre}`;
  if (hora >= 12 && hora <= 14) return `Hola${nombre}`;
  if (hora >= 15 && hora <= 19) return `Buenas tardes${nombre}`;
  return `Buenas noches${nombre}`;
}

// Día de la semana en minúscula para la línea "Próximo turno: el martes".
const DIAS_SEMANA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

// Animación sutil del icono en las tarjetas de Explora (solo con sesión).
const ICON_ANIM = {
  'pae-rapido':    'anim-sparkle',
  'pae-detallado': 'anim-pen',
  revisa:          'anim-zap',
  escalas:         'anim-ecg',
  medicamentos:    'anim-pill',
  'prepara-turno': 'anim-book',
};

// ─── Progreso widget ─────────────────────────────────────────────────────────

// ¿Hay algo que mostrar en "Tu progreso"? Vacío = las TRES cifras en cero (o
// sin dato). Antes bastaba con 0 PAE, y a quien usa la app hace semanas sin
// haber guardado un PAE se le escondían sus días activos y reflexiones (A21).
// Exportada: la misma regla manda en Mi progreso.
export function progresoVacio(stats) {
  if (!stats) return true;
  return !stats.totalPaes && !stats.bitacoraCount && !stats.activeDaysCount;
}

// Solo formación (2026-09-14): la capa de ejercicio ya no lo ve, ver el
// render. Anti-culpa como siempre: celebra lo hecho, jamás acusa lo que falta.
// `onMisPaes` (2026-09-10): el tile "PAEs" lleva a la pantalla Tus PAEs cuando
// el rol la tiene (estudiantes e internos); si no, cae a Mi progreso como antes.
function ProgresoWidget({ userId, onMiProgreso, onMisPaes }) {
  const { stats, loading } = useUserProgress(userId);
  // "Días activos" cuenta solo las últimas 4 semanas (getMyConsistency): el
  // rótulo lo dice para que no se lea como el total de la persona (A21).
  const kpis = [
    { label: 'PAEs',        value: stats?.totalPaes       ?? null },
    { label: 'Reflexiones', value: stats?.bitacoraCount   ?? null },
    { label: 'Días activos en las últimas 4 semanas', value: stats?.activeDaysCount ?? null },
  ];

  // Vacío solo si NO hay nada que mostrar: con 0 PAE y semanas de uso, los
  // días activos y las reflexiones se escondían tras un "—" (A21).
  const isEmpty = !loading && !!stats && progresoVacio(stats);

  return (
    <div className="mt-8">
      <Card padding="md">
        <div className="flex items-start justify-between gap-2">
          <p className="text-body font-semibold text-ink font-sans">Tu progreso</p>
          {onMiProgreso && !isEmpty && (
            <button
              type="button"
              onClick={onMiProgreso}
              className="flex items-center gap-1 text-small text-accent hover:text-accent-hover font-semibold font-sans transition-colors flex-shrink-0"
            >
              Ver todo <ChevronRight size={13} strokeWidth={2} />
            </button>
          )}
        </div>

        {loading ? (
          <div className="mt-3 flex gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex-1 rounded-tk-sm border border-edge p-3 animate-pulse">
                <div className="h-5 w-6 bg-edge rounded mb-1" />
                <div className="h-3 w-16 bg-edge rounded" />
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          <div className="mt-3">
            <div className="grid grid-cols-3 gap-3 mb-3">
              {kpis.map(({ label }) => (
                <div key={label} className="rounded-tk-sm border border-dashed border-edge p-3 text-center">
                  <p className="text-body font-bold text-ink-faint font-sans leading-none">—</p>
                  <p className="text-micro text-ink-muted font-sans mt-1 leading-tight">{label}</p>
                </div>
              ))}
            </div>
            <p className="text-small text-ink-muted font-sans leading-relaxed">
              Cada PAE, reflexión y día activo suma aquí. Empieza y míralo crecer.
            </p>
            {onMiProgreso && (
              <button
                type="button"
                onClick={onMiProgreso}
                className="mt-2 text-small text-accent hover:text-accent-hover font-semibold font-sans transition-colors"
              >
                Ver mi progreso →
              </button>
            )}
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-3 gap-3">
            {/* Sin destino (versión ejercicio), las tiles son SOLO lectura: un
                botón que no hace nada engaña al tacto y al lector de pantalla
                (corrección del panel adversarial, 2026-08-02). */}
            {kpis.map(({ label, value }) => (
              onMiProgreso ? (
                <button
                  key={label}
                  type="button"
                  onClick={label === 'PAEs' && onMisPaes ? onMisPaes : onMiProgreso}
                  className="rounded-tk-sm border border-edge p-3 text-left hover:bg-surface transition-colors"
                >
                  <p className="text-body font-bold text-ink font-sans leading-none">{value ?? '—'}</p>
                  <p className="text-micro text-ink-muted font-sans mt-1 leading-tight">{label}</p>
                </button>
              ) : (
                <div key={label} className="rounded-tk-sm border border-edge p-3 text-left">
                  <p className="text-body font-bold text-ink font-sans leading-none">{value ?? '—'}</p>
                  <p className="text-micro text-ink-muted font-sans mt-1 leading-tight">{label}</p>
                </div>
              )
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Login por correo (código OTP) ───────────────────────────────────────────
// Passwordless, bajo el botón de Google: entra quien no tiene Google o lo olvida
// (fricción #1 del login). Al verificar, Supabase deja la sesión y el
// onAuthStateChange de App.jsx enruta el resto (perfil, gate de edad, home).
// No maneja claves. Arranca COLAPSADO (rediseño editorial de la landing): el
// correo es la segunda puerta y no debe competir visualmente con Google; el
// formulario se revela recién cuando el visitante lo pide.
// `abierto`: en la app de iOS no hay Google (GOOGLE_LOGIN_DISPONIBLE), así que
// el correo es la segunda puerta y se muestra abierto, con el campo a la vista.
// El revisor de Apple buscaba dónde poner la cuenta de prueba y tocaba Google.
// `conClave`: solo en la app de iOS se ofrece además "Entrar con contraseña",
// un formulario de correo y contraseña de los de siempre. Es la puerta de la
// cuenta de prueba del revisor (quinto rechazo, 2026-10-05: pidió el código y
// se quedó esperando un correo que no puede abrir). Ver entrarConClave.
function LoginPorCorreo({ abierto = false, conClave = false }) {
  const [fase, setFase] = useState(abierto ? "correo" : "cerrado"); // 'cerrado' | 'correo' | 'codigo' | 'clave'
  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [clave, setClave] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const pedirCodigo = async (e) => {
    e?.preventDefault();
    setCargando(true);
    setError(null);
    try {
      const { error: err } = await enviarCodigoEmail(email);
      if (err) {
        setError(err.message || "No se pudo enviar el código. Reintenta.");
        return;
      }
      setFase("codigo");
    } catch {
      setError("No pudimos conectarnos. Revisa tu conexión.");
    } finally {
      setCargando(false);
    }
  };

  const entrar = async (e) => {
    e?.preventDefault();
    setCargando(true);
    setError(null);
    try {
      const { error: err } = await verificarCodigoEmail(email, codigo);
      if (err) {
        // Reintento: no reseteamos el código para que corrija sin re-teclear todo.
        setError(err.message || "Código incorrecto o vencido. Pide uno nuevo.");
        return;
      }
    } catch {
      setError("No pudimos conectarnos. Revisa tu conexión.");
    } finally {
      setCargando(false);
    }
    // Éxito: onAuthStateChange (App.jsx) se encarga del resto.
  };

  const entrarClave = async (e) => {
    e?.preventDefault();
    setCargando(true);
    setError(null);
    try {
      const { error: err } = await entrarConClave(email, clave);
      if (err) setError(err.message);
    } catch {
      setError("No pudimos conectarnos. Revisa tu conexión.");
    } finally {
      setCargando(false);
    }
    // Éxito: igual que el código, onAuthStateChange (App.jsx) sigue solo.
  };

  const inputCls =
    "w-full rounded-tk-md border border-edge bg-paper px-4 py-3 text-body text-ink font-sans placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent";

  // Colapsado: el divisor entero es el botón que revela el formulario. Así el
  // "o con tu correo" conserva su lugar tipográfico pero pesa lo mínimo, y el
  // target táctil es la fila completa (≥44px), no solo el texto.
  if (fase === "cerrado") {
    return (
      <button
        type="button"
        onClick={() => setFase("correo")}
        className="mt-4 w-full min-h-[44px] flex items-center gap-3 rounded-tk-sm cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
      >
        <span className="h-px flex-1 bg-edge" aria-hidden="true" />
        <span className="text-small text-ink-subtle dark:text-ink-muted group-hover:text-ink font-sans transition-colors">
          o continúa con tu correo
        </span>
        <span className="h-px flex-1 bg-edge" aria-hidden="true" />
      </button>
    );
  }

  return (
    // animate-cruce (opacity, cubierta por prefers-reduced-motion): el
    // formulario entra con un fundido corto al revelarse, sin desplazar nada.
    <div className="mt-4 animate-cruce">
      <div className="flex items-center gap-3 mb-3" aria-hidden="true">
        <span className="h-px flex-1 bg-edge" />
        <span className="text-small text-ink-muted font-sans">con tu correo</span>
        <span className="h-px flex-1 bg-edge" />
      </div>

      {fase === "correo" ? (
        <form onSubmit={pedirCodigo} className="flex flex-col gap-2">
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            // Solo se monta tras un tap explícito del usuario: el foco
            // automático continúa su gesto, no lo interrumpe.
            autoFocus
            placeholder="tu@correo.cl"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            aria-label="Tu correo"
          />
          <Button type="submit" variant="secondary" size="lg" disabled={cargando} className="w-full">
            {cargando ? "Enviando…" : "Enviar código"}
          </Button>
          <p className="text-small text-ink-muted font-sans text-center">
            Sin contraseña. Te enviamos un código de un solo uso.
          </p>
          {conClave && (
            <Button
              type="button"
              variant="ghost"
              size="lg"
              className="w-full"
              onClick={() => { setFase("clave"); setError(null); }}
            >
              Entrar con contraseña
            </Button>
          )}
        </form>
      ) : fase === "clave" ? (
        <form onSubmit={entrarClave} className="flex flex-col gap-2">
          <input
            type="email"
            inputMode="email"
            autoComplete="username"
            placeholder="tu@correo.cl"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            aria-label="Correo"
          />
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Contraseña"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            className={inputCls}
            aria-label="Contraseña"
          />
          <Button type="submit" variant="primary" size="lg" disabled={cargando} className="w-full">
            {cargando ? "Entrando…" : "Entrar"}
          </Button>
          <button
            type="button"
            onClick={() => { setFase("correo"); setClave(""); setError(null); }}
            className="self-center min-h-[44px] text-small text-ink-muted hover:text-ink font-sans"
          >
            Prefiero recibir un código
          </button>
        </form>
      ) : (
        <form onSubmit={entrar} className="flex flex-col gap-2">
          <p className="text-small text-ink-subtle font-sans">
            Te enviamos un código a <span className="font-semibold text-ink">{email}</span>. Escríbelo aquí:
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={32}
            placeholder="________"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
            className={`${inputCls} text-center tracking-[0.5em]`}
            aria-label="Código de 6 dígitos"
          />
          <Button type="submit" variant="primary" size="lg" disabled={cargando} className="w-full">
            {cargando ? "Entrando…" : "Entrar"}
          </Button>
          <button
            type="button"
            onClick={() => { setFase("correo"); setCodigo(""); setError(null); }}
            className="self-center text-small text-ink-muted hover:text-ink font-sans"
          >
            Usar otro correo
          </button>
        </form>
      )}

      {error && (
        <p className="mt-2 text-center text-small text-system-error font-sans" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── StepHome ─────────────────────────────────────────────────────────────────
export function StepHome({
  onSelectMode, onDemo, onPrivacidad, onTerminos, onQueEsMiPAE, onContacto, onInstituciones,
  onPreparaPractica, onPreparaInternado, onAbrirRevisa, onAbrirEscalas, onAbrirMedicamentos, onAbrirGes, onAbrirReferencia, onAbrirRecepcion, onAbrirEstudio, onAbrirValoresLab, onAbrirAislamiento, onAbrirModelos, onAbrirProcedimientos, onAbrirReto, onAbrirBibliografia, onPreparaTurno, onRegistroTurno, camaActiva, onRegistrarEnCama, onAbrirBitacora, onAbrirRespira, onAbrirBienestar, onLogin, onLoginApple, userEmail, userName,
  profile, userId, rotacion: rotacionCasa, onConfigCalendario, onVerCalendario,
  onMiProgreso, onMisPaes,
  onIrPerfil,
  recientes = [],
  onAbrirReciente,
  homeNudges = [],
  onNudgeAction,
  onNudgeDismiss,
  onAbrirCompanero,
  onCompaneroPrompt,
  // Plan de la interfaz de los tres mundos (2026-10-04). `deVisita`: la
  // persona mira otro mundo y ve la versión simple (2.5). La card del
  // estudiante la decide App (`conMomentoEstudiante`) porque también decide
  // si el aviso del borrador va arriba: un solo lugar, sin repetirlo.
  deVisita = false,
  conMomentoEstudiante = false,
  borrador = null,
  onContinuarBorrador,
  onDescartarBorrador,
  // La invitación del 25 (membresias/AvisoLoNuevo.jsx): a dónde lleva el
  // aviso y qué hacer cuando se activa la prueba desde él.
  onLoNuevo,
  onTrialIniciado,
  // Sin sesión, la membresía que la persona eligió en /precios antes de
  // entrar (lib/intencionMembresia.js): la tarjeta de acceso lo dice, para
  // que no parezca que el botón la trajo a otra parte. Solo web.
  membresiaPendiente = null,
}) {
  const isLoggedIn = !!userEmail;
  // La rotación es de la CASA: de visita en otro mundo no hay turno, semana
  // ni "Tu rotación en…" (plan de la interfaz, 2.5), y tampoco el saludo de
  // Benito puede anunciar el turno de quien vive en la casa. Con null aquí,
  // todo lo que se arma desde la rotación queda callado de visita.
  const rotacion = deVisita ? null : rotacionCasa;
  // Decide si abrir un módulo o redirigir a login, siempre vía helper.
  const abrirOLogin = (id, fn) => puedeAbrir(id, { isLoggedIn }) ? fn?.() : onLogin();

  // ── Landing por etapa (auditoría 2026-07-06) ──────────────────────────────
  // El 74% de quienes se registran está en formación, pero la landing era una
  // sola para todos y su único gancho era el caso PAE de ejemplo. El visitante
  // elige su etapa y el subtítulo + CTA secundario le hablan a ella; la
  // elección persiste para la próxima visita. La telemetría del visitante va
  // por el canal anónimo (lib/landingTracking → eventos_anonimos): eventos_v2
  // exige user_id y aquí aún no hay sesión.
  const [etapaLanding, setEtapaLanding] = useState(() => {
    try { return localStorage.getItem("mipae:landing:etapa") || null; } catch { return null; }
  });
  const landingVistaDespachada = useRef(false);
  useEffect(() => {
    if (isLoggedIn || landingVistaDespachada.current) return;
    landingVistaDespachada.current = true;
    trackLandingVista(etapaLanding);
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps
  const elegirEtapaLanding = (id) => {
    const next = etapaLanding === id ? null : id;
    setEtapaLanding(next);
    if (next) trackLanding("landing_etapa", { etapa: next });
    try {
      if (next) localStorage.setItem("mipae:landing:etapa", next);
      else localStorage.removeItem("mipae:landing:etapa");
    } catch { /* almacenamiento bloqueado: la elección vive solo en memoria */ }
  };
  // Textos revisados el 2026-09-29 con la guía editorial: cada uno dice qué
  // hace MiPAE en esa etapa, sin prometer resultados que no están medidos
  // (salir más temprano, llegar con más confianza) ni funciones apagadas
  // (la dupla TENS con la enfermera está desconectada desde el 2026-08-10).
  const LANDING_POR_ETAPA = {
    default: {
      sub: "Una app para estudiar, fundamentar tu PAE, organizar el registro del turno y consultar escalas, fármacos y cálculos cuando surge la duda.",
      cta: "Ver caso de ejemplo",
      ctaId: "demo",
      onCta: onDemo,
    },
    estudiante: {
      sub: "El primer caso clínico impone. Con MiPAE armas el PAE paso a paso, ves el razonamiento clínico detrás de cada diagnóstico y tienes las escalas a mano.",
      cta: "Ver un PAE de ejemplo",
      ctaId: "demo",
      onCta: onDemo,
    },
    // El texto del interno habla del registro del turno: su botón abre ESE
    // ejemplo (DemoPublica en su variante de registro), no el PAE (A27).
    interno: {
      sub: "MiPAE está pensado para el internado. Te preparas antes de llegar, registras el turno mientras pasa y armas la entrega desde ese mismo registro.",
      cta: "Ver un registro de ejemplo",
      ctaId: "demo",
      onCta: () => onDemo?.("interno"),
    },
    enfermero: {
      sub: "Registra lo que observas y realizas durante el turno. MiPAE te ayuda a organizar esa información para revisar tu evolución y preparar la entrega, con las escalas y los fármacos a mano.",
      cta: "Ver una evolución de ejemplo",
      ctaId: "demo",
      onCta: () => onDemo?.("enfermero"),
    },
    tens: {
      sub: "Tienes un registro a tu medida para los cuidados que realizas y lo que observas del paciente, como los cambios de posición, cada uno con su hora.",
      cta: "Ver un registro de cuidados",
      ctaId: "demo",
      onCta: () => onDemo?.("tens"),
    },
  };
  const landing = LANDING_POR_ETAPA[etapaLanding] || LANDING_POR_ETAPA.default;
  // ── Banner calendario ────────────────────────────────────────────────────────
  // Incluye práctica de pregrado además del internado (helper compartido).
  const esElegible = esElegibleTurno(profile);
  // "Tu semana" también para el TENS (propuesta de la interfaz por
  // experiencia, 2026-10-16): trabaja por turnos y su calendario es el de la
  // app. Solo la semana: EL MOMENTO del turno sigue siendo de los elegibles,
  // porque el registro del TENS es otro (lib/tens.js).
  const conSemana = esElegible || esTens(profile);

  // Overrides del calendario: desde el LUNES de esta semana hasta +14 días en
  // una sola consulta. El de hoy alimenta el cockpit (como siempre); los
  // futuros, "Tu semana" y el próximo turno; y los días YA pasados de la
  // semana también van con su override (panel 2026-08-02: la tira pintaba el
  // lunes pasado con el patrón puro aunque el usuario lo hubiera cambiado —
  // jamás afirmar un turno que el calendario ya no tiene, ni en pasado).
  const [overridesRango, setOverridesRango] = useState([]);
  useEffect(() => {
    if (!isLoggedIn || !conSemana || !rotacion || !userId) return;
    const hasta = new Date();
    hasta.setDate(hasta.getDate() + 14);
    supabase
      .from('turno_overrides')
      .select('*')
      .eq('user_id', userId)
      .gte('fecha', toDateKey(inicioDeSemana(new Date())))
      .lte('fecha', toDateKey(hasta))
      .then(({ data }) => setOverridesRango(Array.isArray(data) ? data : []));
  }, [isLoggedIn, conSemana, rotacion, userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const overrideHoy = overridesRango.find((o) => o.fecha === toDateKey(new Date())) || null;

  // Llegada desde el QR de un curso (lib/cursoPaliativos.js, CURSOS):
  // sin sesión, el caso va arriba de la portada; con sesión, en el inicio
  // hasta que la persona lo cierre o pase la fecha. Cada curso trae su caso.
  const [cursoVisitante] = useState(() => cursoActivo());
  const [cursoConSesion, setCursoConSesion] = useState(() => cursoActivo({ conSesion: true }));
  const irAAccesoDesdeCurso = () => {
    const el = typeof document !== "undefined" ? document.getElementById("acceso-mipae") : null;
    if (!el) return;
    const quieto = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    el.scrollIntoView?.({ behavior: quieto ? "auto" : "smooth", block: "center" });
    el.querySelector("button")?.focus({ preventScroll: true });
  };
  const preguntarCasoCurso = (texto) => {
    trackEvent('curso_caso_benito', { modulo: MODULOS.HOME, payload: { campana: cursoConSesion?.campana } });
    onCompaneroPrompt?.(texto);
  };
  const cerrarCasoCurso = () => {
    trackEvent('curso_caso_cerrado', { modulo: MODULOS.HOME, payload: { campana: cursoConSesion?.campana } });
    if (cursoConSesion) cerrarCurso(cursoConSesion.campana);
    setCursoConSesion(null);
  };

  // CTA fade when auth state changes (skip first render)
  const [ctaVisible, setCtaVisible] = useState(true);
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setCtaVisible(false);
    const t = setTimeout(() => setCtaVisible(true), 150);
    return () => clearTimeout(t);
  }, [isLoggedIn]);

  // Saludo personalizado: preferir profiles.nombre, luego user_metadata, luego email
  const primerNombre = profile?.nombre
    ? profile.nombre.trim().split(/\s+/)[0]
    : userName
      ? userName.trim().split(/\s+/)[0]
      : userEmail?.split('@')[0] || '';

  // ── Motor del turno: ¿hay turno en curso o pacientes en seguimiento? ──────────
  const ahoraHero = new Date();
  const turnoHoyHero = (esElegible && rotacion) ? calcularTurnoDelDia(ahoraHero, rotacion, overrideHoy) : null;
  // Solo para el saludo del Dieciocho (17 de septiembre): saber si MAÑANA hay
  // turno. Mismo lookup de override que `proximoTurnoLinea`, para no escribir
  // una segunda regla de calendario. Sin rotación devuelve null y el saludo
  // cae al neutro, que no afirma turno de nadie.
  // Desde el 2026-09-28 (E12) también alimenta el saludo de Benito con la
  // HORA del turno de mañana: el objeto se calcula una vez y de él salen el
  // booleano de siempre y la hora (null sin rotación, nunca se inventa).
  const turnoMananaObj = (() => {
    if (!esElegible || !rotacion) return null;
    const d = new Date(ahoraHero);
    d.setDate(d.getDate() + 1);
    const ov = overridesRango.find((o) => o.fecha === toDateKey(d)) || null;
    const t = calcularTurnoDelDia(d, rotacion, ov);
    return (t && t.tipo !== 'libre' && t.horaInicio) ? t : null;
  })();
  const turnoMananaHero = (esElegible && rotacion) ? !!turnoMananaObj : null;
  const registrosVivosHero = (() => {
    if (!userId) return [];
    try { return purgarExpirados(cargarRegistros(userId)).vivos; }
    catch { return []; }
  })();
  const pacientesActivos = registrosVivosHero.filter((r) => !r.cerrado).length;
  // Cierre del día: cuando el turno terminó y hubo pacientes, ofrecemos el cierre
  // (resumen + descarga emocional). Resumen sobre todos los del turno (incl.
  // cerrados) pero SIN la cama de práctica (auditoría 2026-08-01: los datos
  // demo no son jornada real y ahora Benito comenta este resumen; misma
  // exclusión que ya aplica benitoContexto).
  const resumenTurnoHero = resumenTurno(registrosVivosHero.filter((r) => !r.practica));
  // Ancla post-noche ("blindar el cierre", fix d): si AYER fue turno de noche
  // o de 24h y todavía no pasa su horaFin + ventana de cierre, la fase se
  // ancla a ESE turno aunque la rotación de hoy ya sea otra cosa — así
  // mostrarCierreTurno (resumen + sello) aparece para quien llega de una
  // noche. `turnoHoyHero` (countdown, CockpitTurno) no cambia; solo cambia
  // qué fase gatilla el cierre.
  const faseHero = esElegible
    ? faseDelTurnoActual(rotacion, turnoHoyHero, ahoraHero)
    : faseDelTurno(turnoHoyHero, ahoraHero);
  const rondaHero = (() => {
    if (!userId) return null;
    try {
      const r = cargarRonda(userId);
      return r && Date.now() - r.inicio < 12 * 3600 * 1000 ? r : null;
    } catch { return null; }
  })();
  // El cockpit aparece siempre que haya turno hoy (no libre) o pacientes en
  // seguimiento — así MiPAE en turno tiene presencia constante en el home, no solo
  // con el turno ya abierto.
  const hayTurnoHoy = !!(turnoHoyHero && turnoHoyHero.tipo !== 'libre' && turnoHoyHero.horaInicio);
  const mostrarHeroTurno = isLoggedIn && esElegible && (hayTurnoHoy || pacientesActivos > 0);
  // El cierre ya descartado hoy (X de la card, device-local) no puede seguir
  // reclamando el puesto de protagonista: CierreTurno renderizaba null y la
  // rama del cockpit quedaba suprimida toda la fase de cierre (barrido
  // 2026-08-02) — el Home se quedaba sin MiPAE en turno hasta el día siguiente.
  const cierreDescartadoHoy = getCierreTurnoVisto(userId) === fechaHoyKey();
  const mostrarCierreTurno = isLoggedIn && esElegible && faseHero === 'cierre' && resumenTurnoHero.totalPacientes > 0 && !cierreDescartadoHoy;
  const mostrarCargaRotativa = !!(profile?.perfil_completado && esElegible && !rotacion && onConfigCalendario);
  // La interna o el interno en curso (propuesta de la interfaz por
  // experiencia, 2026-10-16). Con turno hoy, EL MOMENTO es el turno y su PAE
  // sin terminar baja a una fila debajo; sin turno, abre como estudiante
  // (MomentoEstudiante), que también trae el borrador. Las mismas tres ramas
  // del render (cierre, cockpit, activar): si cualquiera se pinta, hay turno.
  const esInterna = profile?.rol === 'estudiante' && esElegible;
  const hayMomentoTurno = mostrarCierreTurno || mostrarHeroTurno
    || !!(camaActiva && onRegistrarEnCama) || (!camaActiva && mostrarCargaRotativa);
  const momentoInterna = isLoggedIn && esInterna && !hayMomentoTurno;
  const filaPaeInterna = isLoggedIn && esInterna && hayMomentoTurno && !!borrador;
  // Zona "Hoy": solo se muestra si hay algo accionable del momento.
  // El nudge de rotación CEDE ante la card "Activa MiPAE en turno" cuando ambas
  // aplican (auditoría 2026-07-31): misma condición y mismo destino — dos
  // cards pidiendo lo mismo se leía como "no entiendo qué me pide".
  const nudgeHome = (homeNudges[0]?.id === 'primer_viaje_rotativa' && mostrarCargaRotativa) ? null : homeNudges[0];
  // ── Benito VIVO en el hero (retoque pre-Play, 2026-08-02) ────────────────────
  // El MISMO panorama del panel (saludoBenito/chipsBenito de benitoContexto,
  // cero IA), calculado al render: el Home se re-monta al volver, así el saludo
  // no envejece. El saludo grande por hora ya vive arriba del todo; Benito abre
  // con su "Hola." propio. Solo hechos de catálogo cerrado, jamás texto libre.
  const vigenteBenito = (isLoggedIn && esElegible) ? turnoVigente(rotacion, ahoraHero, overrideHoy) : null;
  const resumenBenito = isLoggedIn ? resumenTurnoVivo(registrosVivosHero, ahoraHero) : null;
  const fueraDiasBenito = (isLoggedIn && userId) ? diasFuera(diasActivosLocal(userId)) : null;
  const saludoHeroBenito = saludoBenito({
    saludoHora: 'Hola.',
    vigente: vigenteBenito,
    resumen: resumenBenito,
    fueraDias: fueraDiasBenito,
    // Solo se afirma "hoy tienes libre" cuando de verdad lo sabemos (hay
    // rotación cargada). Sin ella va null y el saludo del Dieciocho cae al
    // neutro: no tener turno REGISTRADO no significa estar libre.
    libreHoy: (esElegible && rotacion) ? !hayTurnoHoy : null,
    turnoManana: turnoMananaHero,
    horaTurnoManana: turnoMananaObj?.horaInicio || null,
  });
  // Dos chips en el Home: es una oferta, no un menú (el panel muestra los tres
  // de siempre). Tocar un chip abre el panel con el mensaje PRELISTO (prefill:
  // Benito jamás se abre ni envía solo). A telemetría viaja SOLO el id.
  // "Seguir donde quedamos" (paso 2 de la interfaz por experiencia,
  // 2026-10-16): el mismo chip del panel, con la misma regla. Solo con una
  // conversación que ya venció (una vigente se restaura entera al abrir) y
  // siempre de prefill: retomar es decisión de la persona.
  const temaRetomar = (isLoggedIn && userId) ? temaParaRetomar(cargarHilo(userId)) : null;
  // El turno del TENS de este dispositivo (card "Panorama"): se lee una vez por
  // visita al inicio, no en cada render. El inicio se vuelve a montar al volver.
  const conMomentoTens = !deVisita && isLoggedIn && !!userId && esTens(profile);
  const servicioPerfil = profile?.servicio || null;
  // El turno que dice el calendario, con el ancla post-noche y los cambios a
  // mano: la card nombra el turno y su hora de término SOLO si coincide con el
  // registrado (jamás afirmar un turno que no se sabe, regla de TarjetaHoy).
  const calendarioTens = (() => {
    if (!conMomentoTens || !rotacion) return null;
    const ancla = turnoAnclaPostNoche(rotacion, ahoraHero, overridesRango);
    if (ancla) return { tipo: ancla.turno.tipo, horaFin: ancla.turno.horaFin, fase: faseDelTurno(ancla.turno, ahoraHero, ancla.base) };
    const t = calcularTurnoDelDia(ahoraHero, rotacion, overrideHoy);
    if (!t || t.tipo === 'libre' || !t.horaInicio) return null;
    return { tipo: t.tipo, horaFin: t.horaFin, fase: faseDelTurno(t, ahoraHero) };
  })();
  // Día libre seguro según su calendario: sin un turno empezado, la card no
  // invita a empezar uno (como el inicio del enfermero en su día libre).
  const libreHoyTens = conMomentoTens && !!rotacion && !calendarioTens
    && calcularTurnoDelDia(ahoraHero, rotacion, overrideHoy)?.tipo === 'libre';
  const resumenTens = useMemo(
    () => (conMomentoTens ? leerResumenTurnoTens(userId, { servicioPerfil, calendario: calendarioTens }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conMomentoTens, userId, servicioPerfil, calendarioTens?.tipo, calendarioTens?.horaFin, calendarioTens?.fase],
  );
  const chipsHeroBenito = (isLoggedIn && onAbrirCompanero)
    ? chipsBenito({ vigente: vigenteBenito, resumen: resumenBenito, temaAnterior: temaRetomar, elegibleTurno: esElegible, tens: esTens(profile) }).slice(0, 2)
    : [];
  const elegirChipBenito = (c) => {
    trackEvent('companero_global_chip', { modulo: MODULOS.COMPANERO_GLOBAL, payload: { chip: c.id, prefill: true, origen: 'home' } });
    if (onCompaneroPrompt) onCompaneroPrompt(c.t);
    else onAbrirCompanero?.();
  };

  // ── Lo que el Home ofrece por CAPA (lib/homeExplora.js, puro y testeado) ───
  // Aquí solo se traduce el catálogo a icono + callback; QUÉ se ofrece a quién
  // lo decide el dominio, que es el mismo criterio del menú y del buscador.
  const ICONO_ACTIVIDAD = {
    reto:     <Zap size={15} strokeWidth={1.8} className="text-accent" />,
    turno:    <ClipboardList size={15} strokeWidth={1.8} className="text-accent" />,
    pae:      <ClipboardCheck size={15} strokeWidth={1.8} className="text-accent" />,
  };
  // "Arma un PAE" abre el PAE Rápido directo, por la puerta de siempre (A24):
  // antes abría a Benito con el texto escrito, un paso de más para lo que la
  // actividad promete.
  const ACCION_ACTIVIDAD = {
    reto:     () => onAbrirReto?.(),
    turno:    () => onRegistroTurno?.(),
    pae:      () => onSelectMode?.("rapido", "racha"),
  };
  const ICONO_EXPLORA = {
    'pae-rapido':    (size, anim) => <Sparkles size={size} strokeWidth={1.6} className={anim} />,
    // Los íconos de lo que vive en la Enciclopedia son los de la Enciclopedia
    // (ReferenciaRapida), y el menú y el buscador dicen lo mismo.
    modelos:         (size, anim) => <Library size={size} strokeWidth={1.6} className={anim} />,
    estudio:         (size, anim) => <GraduationCap size={size} strokeWidth={1.6} className={anim} />,
    laboratorio:     (size, anim) => <FlaskConical size={size} strokeWidth={1.6} className={anim} />,
    revisa:          (size, anim) => <Zap size={size} strokeWidth={1.6} className={anim} />,
    escalas:         (size, anim) => <Activity size={size} strokeWidth={1.6} className={anim} />,
    medicamentos:    (size, anim) => <Pill size={size} strokeWidth={1.6} className={anim} />,
    // Mismo ícono que Procedimientos en la Enciclopedia.
    cuidados:        (size, anim) => <ListChecks size={size} strokeWidth={1.6} className={anim} />,
    // Mismo ícono que Aislamiento y EPP en la Enciclopedia.
    aislamiento:     (size, anim) => <ShieldAlert size={size} strokeWidth={1.6} className={anim} />,
  };

  // ── Próximo turno en UNA línea (poda pre-Play: el calendario colapsa) ───────
  // El widget completo sigue en la vista de calendario; el Home solo necesita
  // saber cuándo te toca de nuevo. Sin overrides futuros (best-effort barato).
  const proximoTurnoLinea = (() => {
    if (!conSemana || !rotacion) return null;
    for (let i = 1; i <= 14; i++) {
      const d = new Date(ahoraHero);
      d.setDate(d.getDate() + i);
      const ov = overridesRango.find((o) => o.fecha === toDateKey(d)) || null;
      const t = calcularTurnoDelDia(d, rotacion, ov);
      if (t && t.tipo !== 'libre' && t.horaInicio) {
        return {
          // Más allá de la semana, el nombre del día solo ("el miércoles") se
          // lee como el más cercano: se desambigua con el número del día.
          dia: i === 1 ? 'mañana' : i <= 6 ? `el ${DIAS_SEMANA[d.getDay()]}` : `el ${DIAS_SEMANA[d.getDay()]} ${d.getDate()}`,
          tipo: TIPO_TURNO_LABEL[t.tipo] || 'Turno',
          horario: t.horaFin ? `${t.horaInicio}–${t.horaFin}` : t.horaInicio,
        };
      }
    }
    return null;
  })();

  const handleTarjetaClick = (id) => {
    if (id === "rapido")            onSelectMode("rapido");
    else if (id === "detallado")    onSelectMode("detallado");
    else if (id === "revisa")       onAbrirRevisa();
    else if (id === "prepara-practica")  onPreparaPractica?.();
    else if (id === "prepara-internado") onPreparaInternado();
    else if (id === "prepara-turno") onPreparaTurno?.();
    else if (id === "registro-turno") onRegistroTurno?.();
    else if (id === "bitacora")     onAbrirBitacora?.();
    else if (id === "escalas")      onAbrirEscalas?.();
    else if (id === "medicamentos") onAbrirMedicamentos?.();
    else if (id === "ges")          onAbrirGes?.();
    else if (id === "referencia")   onAbrirReferencia?.();
    else if (id === "recepcion")    onAbrirRecepcion?.();
    else if (id === "estudio")      onAbrirEstudio?.();
    else if (id === "laboratorio")  onAbrirValoresLab?.();
    else if (id === "aislamiento")  onAbrirAislamiento?.();
    else if (id === "modelos")      onAbrirModelos?.();
    else if (id === "procedimientos") onAbrirProcedimientos?.();
    else if (id === "biblioteca")   onAbrirBibliografia?.();
    else if (id === "respira")      onAbrirRespira?.();
  };

  // Tarjeta de acceso del visitante: Apple (solo iPhone), Google y correo en
  // una sola superficie. Vive aquí porque BotonApple y LoginPorCorreo son de
  // este archivo; PortadaPublica solo decide dónde se ubica.
  const tarjetaAcceso = (
    <div className="rounded-tk-lg border border-edge bg-surface p-5 shadow-tk-sm">
      {membresiaPendiente ? (
        <p className="text-small text-ink font-sans text-center mb-4">
          Para activar {membresiaPendiente}, entra o crea tu cuenta. Después sigues con tu membresía.
        </p>
      ) : (
        <p className="text-small text-ink-subtle dark:text-ink-muted font-sans text-center mb-4">
          Crea tu cuenta gratis o entra
        </p>
      )}
      <BotonApple
        onLoginApple={onLoginApple}
        onTrack={() => trackLanding("landing_cta", { etapa: etapaLanding, cta: "login_apple" })}
      />
      {/* En la app de iOS no hay Google (ver GOOGLE_LOGIN_DISPONIBLE). */}
      {GOOGLE_LOGIN_DISPONIBLE && (
        <Button
          variant="primary"
          size="lg"
          iconLeft={<GoogleIcon />}
          onClick={() => { trackLanding("landing_cta", { etapa: etapaLanding, cta: "login_google" }); onLogin(); }}
          className="w-full min-h-[44px]"
        >
          Continuar con Google
        </Button>
      )}
      <LoginPorCorreo abierto={!GOOGLE_LOGIN_DISPONIBLE} conClave={!GOOGLE_LOGIN_DISPONIBLE} />
    </div>
  );

  return (
    <div className="min-h-screen bg-paper">
      {/* ── PORTADA PÚBLICA (sin sesión) ──
          Rediseño 2026-09-29 (components/presentacion/PortadaPublica.jsx):
          ancho propio y franjas de borde a borde, por eso va fuera de la
          columna del Home logueado. El acceso, la etapa y la telemetría
          anónima siguen cableados desde aquí. */}
      {!isLoggedIn && cursoVisitante && <BienvenidaCurso curso={cursoVisitante} onIrAAcceso={irAAccesoDesdeCurso} />}
      {!isLoggedIn && (
        <PortadaPublica
          etapa={etapaLanding}
          onElegirEtapa={elegirEtapaLanding}
          landing={landing}
          acceso={tarjetaAcceso}
          accesoVisible={ctaVisible}
          onQueEsMiPAE={onQueEsMiPAE}
          onContacto={onContacto}
          onInstituciones={onInstituciones}
          onDemo={onDemo}
          onPrivacidad={onPrivacidad}
          abrirOLogin={abrirOLogin}
          onSelectMode={onSelectMode}
          onAbrirEscalas={onAbrirEscalas}
          onAbrirMedicamentos={onAbrirMedicamentos}
          onAbrirReferencia={onAbrirReferencia}
          onAbrirEstudio={onAbrirEstudio}
          onPreparaPractica={onPreparaPractica}
          onPreparaInternado={onPreparaInternado}
          onPreparaTurno={onPreparaTurno}
          onAbrirRespira={onAbrirRespira}
          onAbrirBienestar={onAbrirBienestar}
          onAbrirRevisa={onAbrirRevisa}
          onAbrirBitacora={onAbrirBitacora}
          onAbrirBibliografia={onAbrirBibliografia}
        />
      )}
      {/* En escritorio la columna se ensancha: 480 px es la medida del pulgar,
          no la de un monitor. El Home es una pila de cards, así que no lleva
          riel: lo que gana es aire y que las cards dejen de verse apretadas. */}
      <div className={`mx-auto px-5 max-w-[480px]${SHELL_ESCRITORIO_ENABLED ? " lg:max-w-[760px]" : ""}`}>

        {/* ── WORKSPACE: Saludo + 3 zonas (Hoy · Tus herramientas · Tu camino) ── */}
        {isLoggedIn && (
          <section className="pt-10 pb-8">
            {/* Los banderines del Dieciocho abren el Home. Aquí no va texto de
                la fecha: el saludo de Benito, más abajo, ya lo dice y sabe del
                turno de la persona. Se retiran solos pasado el 19. */}
            <Banderines className="mb-4" />
            {/* La puerta al panel de la fecha. Aquí va el ENLACE y nada más:
                el saludo lo da Benito más abajo, que sí sabe del turno de la
                persona, así que repetirlo aquí sería decir dos veces lo mismo. */}
            <EnlaceDieciocho className="mb-6" />
            {/* Bienvenida: el saludo grande. Sin eslogan del segmento debajo
                (propuesta de la interfaz por experiencia, 2026-10-16): con
                sesión, la persona ya sabe dónde está y la protagonista de
                abajo le dice qué hacer. */}
            <Heading level="h1" editorial className="text-ink">
              {getSaludoPorHora(new Date().getHours(), primerNombre || undefined)}
            </Heading>
            {/* Sin "Día N de tu rotación" (pedido del fundador 2026-08-02: no
                se veía bien); el contexto de servicio y hospital se queda. De
                visita no hay rotación (es de la casa). */}
            {rotacion && (nombreServicio(rotacion) || rotacion.hospital) && (
              <p className="mt-2 text-body text-ink-subtle font-sans">
                Tu rotación
                {nombreServicio(rotacion) ? ` en ${nombreServicio(rotacion)}` : ""}
                {rotacion.hospital ? ` · ${rotacion.hospital}` : ""}
              </p>
            )}

            {/* Quien llegó por el QR de un curso encuentra aquí el caso de su
                video. El botón deja la pregunta escrita en
                Benito (jamás se abre ni la envía solo). */}
            {!deVisita && cursoConSesion && onCompaneroPrompt && (
              <CasoCursoCard curso={cursoConSesion} onPreguntar={preguntarCasoCurso} onCerrar={cerrarCasoCurso} className="mt-6" />
            )}

            {/* ── EL MOMENTO MANDA (retoque pre-Play, 2026-08-02): el Home abre
                con UNA card protagonista según la fase real del turno —
                saliendo → el cierre con tus números arriba (antes quedaba a
                media página); en turno o por partir → el cockpit, con la
                vuelta a tu paciente ADENTRO (antes eran dos cards compitiendo
                por el mismo destino); sin rotación → activar MiPAE en turno, con el
                primer turno guiado a un toque. El orden del DOM ES el flujo:
                nada de reordenar por CSS. */}
            {/* De visita en otro mundo (plan de la interfaz, 2.5) no hay EL
                MOMENTO, ni la semana, la racha, lo reciente, los avisos, la
                encuesta ni el progreso: todo eso es de quien vive ahí, y
                pintarlo con el vocabulario de otro mundo mentiría. Queda el
                saludo, Benito, Explora del mundo y el pie. */}
            {!deVisita && mostrarCierreTurno && (
              <div className="mt-6">
                <CierreTurno
                  resumen={resumenTurnoHero}
                  userId={userId}
                  onAbrirBitacora={onAbrirBitacora}
                  onAbrirRespira={onAbrirRespira}
                  onAbrirTurno={() => onRegistroTurno?.()}
                  onComentarBenito={onCompaneroPrompt ? () => onCompaneroPrompt("Te cuento cómo estuvo mi turno: ") : undefined}
                  ofreceBitacora={!esCapaEjercicio(profile)}
                />
              </div>
            )}
            {!deVisita && !mostrarCierreTurno && (mostrarHeroTurno || (camaActiva && onRegistrarEnCama)) && (
              <div className="mt-6">
                <CockpitTurno
                  variant="home"
                  rotacion={rotacion}
                  turnoHoy={turnoHoyHero}
                  nPacientes={pacientesActivos}
                  ronda={rondaHero}
                  onAbrir={() => onRegistroTurno?.()}
                  accionCama={camaActiva && onRegistrarEnCama ? { cama: camaActiva.cama, onRegistrar: onRegistrarEnCama } : null}
                />
              </div>
            )}
            {!deVisita && !mostrarCierreTurno && !mostrarHeroTurno && !camaActiva && mostrarCargaRotativa && (
              <div className="mt-6 bg-paper border border-edge rounded-tk-md p-5">
                <button
                  type="button"
                  onClick={onConfigCalendario}
                  data-tour="rotativa-card"
                  className="w-full text-left flex items-start gap-3 hover:opacity-90 transition-opacity"
                >
                  <ClipboardList size={20} strokeWidth={1.6} className="text-module-prepara-turno flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-micro text-module-prepara-turno font-sans uppercase tracking-wide">
                      Tu centro de operaciones
                    </p>
                    <p className="mt-1 text-body font-semibold text-ink font-sans">
                      Activa MiPAE en turno
                    </p>
                    <p className="mt-1 text-small text-ink-subtle font-sans leading-relaxed">
                      Carga tu rotación y MiPAE en turno se vuelve tu base: el turno de hoy, tus pacientes y la evolución, todo en un lugar.
                    </p>
                  </div>
                  <ChevronRight size={16} strokeWidth={2} className="text-ink-muted flex-shrink-0 mt-1" />
                </button>
                {/* El primer día no necesita rotación: la cama de práctica del
                    hub (guiada por Benito, jamás sube al remoto ni suma racha)
                    se ofrece también desde acá — quien instala desde Play abre
                    la app en el sillón, no en el turno (pieza 5 del retoque). */}
                {onRegistroTurno && (
                  <button
                    type="button"
                    onClick={() => onRegistroTurno?.()}
                    className="mt-3 w-full min-h-[44px] flex items-center gap-2.5 rounded-tk-sm border border-dashed border-edge px-3 py-2.5 text-left hover:bg-surface transition-colors"
                  >
                    <BenitoAvatar size={24} className="flex-shrink-0" />
                    <span className="flex-1 min-w-0 text-small text-ink font-sans leading-snug">
                      O parte hoy mismo con tu primer turno en una cama de práctica, guiado por Benito.
                    </span>
                    <ChevronRight size={15} strokeWidth={2} className="text-ink-muted flex-shrink-0" />
                  </button>
                )}
              </div>
            )}

            {/* El "qué hago ahora" del estudiante sin internado en curso, en el
                mismo lugar que EL MOMENTO (que a esa capa no le aplica). */}
            {/* La interna o el interno en curso sin turno hoy también abre con
                ella (propuesta de la interfaz por experiencia, 2026-10-16): sin
                turno, su "qué hago ahora" es el de su formación. */}
            {!deVisita && (conMomentoEstudiante || momentoInterna) && (
              <MomentoEstudiante
                userId={userId}
                borrador={borrador}
                onContinuarBorrador={onContinuarBorrador}
                onDescartarBorrador={onDescartarBorrador}
                onAbrirReto={() => onAbrirReto?.()}
                onArmarPae={() => onSelectMode?.("rapido", "momento")}
              />
            )}

            {/* El "qué hago ahora" del TENS (maqueta "Panorama", elegida el
                2026-10-16): su turno de este dispositivo en una tarjeta, o
                empezarlo. De visita no se pinta: es de quien vive aquí. */}
            {conMomentoTens && !(libreHoyTens && !resumenTens) && (
              <MomentoTens
                resumen={resumenTens}
                onAbrirTurno={() => onRegistroTurno?.()}
              />
            )}

            {/* Con su turno arriba, el PAE sin terminar de la interna baja a
                una fila: se retoma sin competir con el turno. */}
            {!deVisita && filaPaeInterna && (
              <FilaPaeSinTerminar borrador={borrador} onContinuar={onContinuarBorrador} onDescartar={onDescartarBorrador} />
            )}

            {/* Tu semana: el calendario con PROTAGONISMO (dictado del fundador
                2026-08-02: era gran atractivo y estaba demasiado silencioso).
                La tira de 7 días con los colores de turno reemplaza a la línea
                plana de "Próximo turno"; tocar un día abre el mes EN ese día.
                Desde el 2026-10-16 también para el TENS, y va antes de Benito:
                lo de hoy y esta semana primero. */}
            {!deVisita && isLoggedIn && conSemana && rotacion && onVerCalendario && (
              <div className="mt-6">
                <SemanaTurnos
                  rotacion={rotacion}
                  overrides={overridesRango}
                  onVerCalendario={onVerCalendario}
                  proximo={proximoTurnoLinea}
                />
              </div>
            )}

            {/* Compañero como entrada principal: el hero VIVO (pieza 2 del
                retoque pre-Play). La cara de Benito (BenitoAvatar acompaña lo
                que Benito DICE; la presencia punto+trazo sigue siendo sus
                estados en pestaña y header) + el saludo real del momento
                (saludoBenito, cero IA, se recalcula en cada render del Home) +
                dos chips vivos que abren el panel con el mensaje prelisto
                (siempre prefill: Benito jamás se abre ni envía solo). */}
            {isLoggedIn && onAbrirCompanero && (
              <div className="mt-6 rounded-tk-lg border border-accent-border bg-accent-bg/40 p-4">
                <button
                  type="button"
                  onClick={onAbrirCompanero}
                  className="w-full flex items-start gap-3 text-left focus:outline-none"
                >
                  <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-paper border border-accent-border flex-shrink-0">
                    <BenitoAvatar size={46} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-h3 font-semibold text-ink font-sans leading-tight">Benito</span>
                    <span className="block mt-0.5 text-small text-ink-subtle font-sans leading-snug">{saludoHeroBenito}</span>
                  </span>
                  <ChevronRight size={18} strokeWidth={2} className="text-ink-muted flex-shrink-0 mt-1" aria-hidden="true" />
                </button>
                {/* El campo que invita a conversar: Benito es un agente, se le
                    escribe o se le habla lo que sea. Abre el compañero en limpio. */}
                <button
                  type="button"
                  onClick={onAbrirCompanero}
                  aria-label="Escríbele o háblale a Benito"
                  className="mt-3 w-full flex items-center gap-2.5 rounded-full border border-accent-border bg-paper px-4 py-2.5 text-left hover:bg-accent-bg/50 transition-colors"
                >
                  <Sparkles size={15} strokeWidth={1.8} className="text-accent flex-shrink-0" />
                  <span className="flex-1 min-w-0 text-small text-ink-muted font-sans truncate">
                    Escríbele o háblale, lo que necesites…
                  </span>
                  <ChevronRight size={16} strokeWidth={2} className="text-ink-muted flex-shrink-0" />
                </button>
                {chipsHeroBenito.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {chipsHeroBenito.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => elegirChipBenito(c)}
                        className="inline-flex items-center min-h-[44px] rounded-full border border-accent-border bg-paper px-3.5 py-1.5 text-small font-medium text-accent font-sans hover:bg-accent-bg/60 transition-colors"
                      >
                        {c.l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Novedades de MiPAE: debajo de lo principal (la protagonista y
                Benito), hasta que la persona las reconoce, y siempre a mano en
                el pie del menú. Antes abrían el inicio y empujaban el turno
                hacia abajo (propuesta de la interfaz por experiencia,
                2026-10-16). Nunca dentro de MiPAE en turno. */}
            {userId && !deVisita && (
              <QueCambioCard
                userId={userId}
                modulo="home"
                className="mt-6"
                profile={profile}
                onLoNuevo={onLoNuevo}
                onPerfil={onIrPerfil}
                onTrialIniciado={onTrialIniciado}
                onTerminos={onTerminos}
              />
            )}

            {/* Atajos: cuatro por experiencia (lib/homeExplora.js), en grilla de
                dos para que quepan sin empujar la pantalla. Sin destacado: la
                protagonista ya está arriba. En MiPAE Enfermero se titulan
                "Consulta rápida" y no llevan el PAE, que vive en MiPAE
                Estudiante. El catálogo completo sigue en la Enciclopedia y el
                menú. */}
            {isLoggedIn && (
              <div className="mt-8 fade-up-2">
                <p className="text-micro text-ink-muted font-sans uppercase tracking-wide mb-3">{tituloAtajos(profile)}</p>
                <div className="grid grid-cols-2 gap-2.5">
                  {tarjetasExplora(profile).map((c) => (
                    <HomeCard
                      key={c.id}
                      layout="mosaico"
                      module={c.module}
                      icon={ICONO_EXPLORA[c.id](20, isLoggedIn ? ICON_ANIM[c.module] : undefined)}
                      label={c.label}
                      description={c.description}
                      onClick={() => abrirOLogin(c.accion, () => handleTarjetaClick(c.accion))}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Gancho diario: la racha de "días seguidos" + "¿qué haces hoy?".
                El usuario elige la actividad; cualquiera que complete (PAE,
                reflexión, turno) marca el día. Menú adaptado a la capa. */}
            {!deVisita && isLoggedIn && userId && (
              <div className="mt-6">
                <RachaDiariaWidget
                  userId={userId}
                  compacta
                  hoyLibre={!!(esElegible && rotacion && turnoHoyHero?.tipo === "libre")}
                  actividades={actividadesRacha(profile).map((a) => ({
                    ...a,
                    icon: ICONO_ACTIVIDAD[a.id],
                    onClick: ACCION_ACTIVIDAD[a.id],
                  }))}
                />
              </div>
            )}

            {/* Sigue donde quedaste: últimas fichas abiertas (GES / fármacos) */}
            {!deVisita && onAbrirReciente && recientes.length > 0 && (
              <div className="mt-4">
                <p className="text-micro text-ink-muted font-sans uppercase tracking-wide mb-2">Sigue donde quedaste</p>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{ WebkitOverflowScrolling: "touch" }}>
                  {recientes.map((it) => (
                    <button
                      key={`${it.tipo}-${it.slug}`}
                      type="button"
                      onClick={() => onAbrirReciente(it)}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-edge bg-paper px-3 py-1.5 text-small font-sans text-ink hover:border-accent/40 transition-colors max-w-[220px]"
                    >
                      {it.tipo === "ges" ? <ShieldCheck size={14} strokeWidth={1.7} className="flex-shrink-0 text-module-ges-text" aria-hidden="true" />
                        : it.tipo === "escala" ? <Activity size={14} strokeWidth={1.7} className="flex-shrink-0 text-ink-muted" aria-hidden="true" />
                        : it.tipo === "lab" ? <Droplets size={14} strokeWidth={1.7} className="flex-shrink-0 text-ink-muted" aria-hidden="true" />
                        : it.tipo === "referencia" ? <BookOpen size={14} strokeWidth={1.7} className="flex-shrink-0 text-accent" aria-hidden="true" />
                        : <Pill size={14} strokeWidth={1.7} className="flex-shrink-0 text-ink-muted" aria-hidden="true" />}
                      <span className="truncate">{it.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Nudge del momento (scope 'home'): el protagonista, Tu semana
                y el cierre viven arriba; aquí solo cae el recordatorio suave
                que corresponda. Sin nudge no se pinta el contenedor. */}
            {!deVisita && nudgeHome && (
              <div className="mt-8 fade-up-2">
                <NudgeCard
                  nudge={nudgeHome}
                  onAction={onNudgeAction}
                  onDismiss={onNudgeDismiss}
                />
              </div>
            )}

            {/* NPS de un toque (ítem 9 del BACKLOG): al pie de la zona viva y
                antes de Tu progreso, para no competir con EL MOMENTO ni con Benito.
                La card decide sola si le toca (cuota mensual por usuario,
                semanal por dispositivo, jamás a mitad de turno, jamás junto a
                novedades sin leer) y desaparece al responder o al descartar. */}
            {/* De visita en otro mundo la encuesta no se muestra (plan de la
                interfaz, 2.5). El `casa` queda como red: si algún día se
                muestra de visita, habla del rol de la casa. */}
            {!deVisita && isLoggedIn && userId && (
              <NpsCard userId={userId} rol={profile?.casa || profile?.rol} perfilCreadoEn={profile?.created_at} modulo="home" className="mt-9" />
            )}

            {/* Tu progreso: momentum de FORMACIÓN (PAEs, reflexiones, días
                activos), con destino a Tus PAEs / Mi progreso. La capa de
                ejercicio no lo ve (decisión del fundador 2026-09-14, revierte
                la pieza 6 del retoque pre-Play): sus tres cifras no llevaban a
                ningún lado y la racha ya vive más arriba en RachaDiariaWidget.
                Un protagonista por momento; la enfermera quiere velocidad. El
                TENS tampoco (2026-10-16): sus cifras son de PAEs y de formación,
                y su registro es otro. */}
            {!deVisita && isLoggedIn && !esCapaEjercicio(profile) && !esTens(profile) && (
              <ProgresoWidget userId={userId} onMiProgreso={onMiProgreso} onMisPaes={onMisPaes} />
            )}
          </section>
        )}

        {/* ── Footer ── */}
        {/* "Con la marca" (Sebastián eligió entre tres maquetas el 2026-10-17,
            docs/pitch/preview-pie-inicio/; "con los colores se ve infantil"):
            el pie lo firma la marca con las redes en gris al lado, y debajo el
            aviso y lo legal en pocas líneas. Sale en el inicio con sesión y en
            la portada de quien no ha entrado. */}
        {/* El id lo usa el WhatsApp flotante de la web pública para apartarse
            cuando el pie (que ya trae WhatsApp) está a la vista. Con sesión el
            WhatsApp del pie abre el mensaje de ayuda. */}
        <footer id="pie-mipae" className="border-t border-edge pt-8 pb-10">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <span className="inline-flex items-center gap-2">
              <MarcaMiPAE />
              <span className="lockup-wordmark text-h3 leading-none text-brand">MiPAE</span>
            </span>
            <SocialBar className="-mr-3" motivo={isLoggedIn ? "ayuda" : "general"} conSesion={isLoggedIn} />
          </div>
          <p className="mt-4 text-small text-ink-subtle dark:text-ink-muted font-sans leading-relaxed">
            MiPAE es una herramienta educativa. No reemplaza el juicio clínico profesional.
          </p>
          {/* "¿Qué es MiPAE?" va en su línea y con el color: en la maqueta iba
              dentro del aviso, pero ahí medía 21 px de alto y el dedo necesita
              44; y sumado a la fila legal, en 360 px la partía dejando un
              punto colgando. */}
          <div className="flex flex-wrap items-center gap-x-5">
            <button
              type="button"
              onClick={onQueEsMiPAE}
              className="inline-flex min-h-[44px] items-center text-small font-semibold text-accent dark:text-accent-light hover:text-ink transition-colors font-sans rounded-tk-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            >
              ¿Qué es MiPAE?
            </button>
            {/* Contacto (2026-10-30): sin sesión, conocer MiPAE o una
                institución; con sesión, pedir ayuda. Misma pantalla. */}
            {onContacto && (
              <a
                href="/contacto"
                onClick={(e) => { e.preventDefault(); onContacto(); }}
                className="inline-flex min-h-[44px] items-center text-small font-semibold text-accent dark:text-accent-light hover:text-ink transition-colors font-sans rounded-tk-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                {isLoggedIn ? "Contacto y ayuda" : "Contacto"}
              </a>
            )}
          </div>
          {/* Anclas reales con href (no botones): la verificación OAuth de Google
              exige que la página principal incluya un vínculo rastreable a la
              política de privacidad. preventDefault mantiene la navegación SPA;
              el href asegura que el crawler (y un usuario sin JS) lleguen igual. */}
          <div className="flex flex-wrap items-center gap-x-1 text-small text-ink-subtle dark:text-ink-muted font-sans">
            <a href="/privacidad" onClick={(e) => { e.preventDefault(); onPrivacidad?.(); }} className="inline-flex min-h-[44px] items-center hover:text-ink transition-colors">Privacidad</a>
            <span className="select-none mx-1" aria-hidden="true">·</span>
            <a href="/terminos" onClick={(e) => { e.preventDefault(); onTerminos?.(); }} className="inline-flex min-h-[44px] items-center hover:text-ink transition-colors">Términos</a>
            <span className="select-none mx-1" aria-hidden="true">·</span>
            <a href="/privacidad" onClick={(e) => { e.preventDefault(); onPrivacidad?.(); }} className="inline-flex min-h-[44px] items-center hover:text-ink transition-colors">Ley 20.584</a>
          </div>
          {/* Seriedad de marca (fundador, 2026-08-02): la sociedad da la cara.
              ™ y no ® A PROPÓSITO: la marca está EN TRÁMITE ante INAPI, y
              declarar "registrada" sin concesión infringe la Ley 19.039.
              Cuando INAPI conceda, este es el lugar del cambio a ®. */}
          <p className="text-micro text-ink-subtle dark:text-ink-muted font-sans">
            © 2026 MiPAE SpA · MiPAE™
          </p>
        </footer>

      </div>
    </div>
  );
}
